import React, { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { api, errorMessage } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Copy, ExternalLink, Download, Link2 } from "lucide-react";
import { toast } from "sonner";

export default function SponsorReferral() {
  const [me, setMe] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setMe(data);
      } catch (e) {
        toast.error(errorMessage(e));
      }
    })();
  }, []);

  if (!me) return <div className="text-sm text-slate-400">Loading…</div>;

  const origin = window.location.origin;
  const slug = me.sponsor_slug || "your-slug";
  const landing = `${origin}/s/${slug}`;
  const referral = `${origin}/apply?ref=${slug}`;

  const copy = async (txt, label) => {
    try { await navigator.clipboard.writeText(txt); toast.success(`${label} copied`); }
    catch { toast.error("Copy failed"); }
  };

  const download = () => {
    const canvas = document.getElementById("qr-canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slug}-qr.png`;
    a.click();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Referral Tools</h1>
        <p className="mt-1 text-sm text-slate-400">Your branded links and QR code.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border-slate-800 bg-slate-900 p-6 lg:col-span-2">
          <div className="mb-5 flex items-center gap-2">
            <Link2 className="h-4 w-4 text-emerald-400" />
            <div className="text-sm font-semibold">Your Links</div>
          </div>
          <div className="space-y-5">
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-widest">Personal Landing Page</Label>
              <div className="mt-2 flex gap-2">
                <Input readOnly value={landing} className="border-slate-800 bg-slate-950 font-mono text-xs text-slate-200" data-testid="landing-url-input" />
                <Button onClick={() => copy(landing, "Landing URL")} variant="outline" className="border-slate-700 bg-slate-950 text-slate-200 hover:bg-slate-800" data-testid="copy-landing-btn">
                  <Copy className="h-4 w-4" />
                </Button>
                <a href={landing} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="border-slate-700 bg-slate-950 text-slate-200 hover:bg-slate-800" data-testid="open-landing-btn">
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </a>
              </div>
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-widest">Direct Referral Link (intake form)</Label>
              <div className="mt-2 flex gap-2">
                <Input readOnly value={referral} className="border-slate-800 bg-slate-950 font-mono text-xs text-slate-200" data-testid="referral-url-input" />
                <Button onClick={() => copy(referral, "Referral URL")} variant="outline" className="border-slate-700 bg-slate-950 text-slate-200 hover:bg-slate-800" data-testid="copy-referral-btn">
                  <Copy className="h-4 w-4" />
                </Button>
                <a href={referral} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="border-slate-700 bg-slate-950 text-slate-200 hover:bg-slate-800" data-testid="open-referral-btn">
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </a>
              </div>
              <div className="mt-2 text-xs text-slate-500">30-day attribution window per click.</div>
            </div>
          </div>
        </Card>

        <Card className="border-slate-800 bg-slate-900 p-6">
          <div className="mb-5 text-sm font-semibold">QR Code</div>
          <div className="flex flex-col items-center gap-4">
            <div className="rounded-md bg-white p-4">
              <QRCodeCanvas id="qr-canvas" value={referral} size={180} bgColor="#ffffff" fgColor="#020617" level="M" />
            </div>
            <Button onClick={download} className="w-full bg-emerald-600 text-white hover:bg-emerald-500" data-testid="download-qr-btn">
              <Download className="mr-2 h-4 w-4" /> Download PNG
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
